'use client';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { GeoPathGeo } from '@/types/geo';
import { geoPaths } from '@/config/geoPaths';
import useOutside from '@/hooks/click/useOutside';
import IntLink from '@/server/link/IntLink';
import styles from './styles.module.css';

const MAX_RESULTS_PER_LOAD = 60;

interface SearchSectionProps {
  title: string;
  description: string;
  placeholder: string;
  noResultsText: string;
}

const SearchSection = ({ title, description, placeholder, noResultsText }: SearchSectionProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPaths, setFilteredPaths] = useState<GeoPathGeo[]>([]);
  const [visiblePaths, setVisiblePaths] = useState<GeoPathGeo[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [currentPage, setCurrentPage] = useState(1); // Page actuelle
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOutside(dropdownRef, () => {
    if (document.activeElement !== inputRef.current) {
      setShowDropdown(false);
    }
  });

  const filteredResults = useMemo(() => {
    if (!searchTerm) return geoPaths;

    const normalizedTerm = searchTerm.toLowerCase().replace(/ /g, '-').replace(/-/g, ' ');

    return geoPaths.filter((geo) => geo.nm.toLowerCase().replace(/-/g, ' ').includes(normalizedTerm) || geo.gkey.startsWith(normalizedTerm));
  }, [searchTerm]);

  // Mettre à jour les résultats visibles selon la pagination
  useEffect(() => {
    const start = 0;
    const end = currentPage * MAX_RESULTS_PER_LOAD;
    setVisiblePaths(filteredPaths.slice(start, end));
  }, [filteredPaths, currentPage]);

  useEffect(() => {
    setFilteredPaths(filteredResults);
    setCurrentPage(1); // Réinitialiser la pagination lorsque les résultats changent
  }, [filteredResults]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setShowDropdown(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (event.key === 'ArrowDown') {
      setActiveIndex((prev) => Math.min(prev + 1, visiblePaths.length - 1));
    } else if (event.key === 'ArrowUp') {
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      const selectedGeo = visiblePaths[activeIndex];
      if (selectedGeo) {
        window.location.href = `/${selectedGeo.slug}`;
      }
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollTop + clientHeight >= scrollHeight) {
      // Charger les résultats suivants
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const highlightText = (text: string, highlight: string): React.ReactNode => {
    if (!highlight) return text;

    const normalizedHighlight = highlight.toLowerCase().replace(/-/g, ' ');

    const regex = new RegExp(`(${normalizedHighlight})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part: string, index: number) =>
      regex.test(part) ? (
        <span key={index} className={styles.highlight}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <section className={styles.searchSection}>
      <div className={styles.container}>
        <h2 className='section_title'>{title}</h2>
        <p className={styles.subheading}>{description}</p>
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            value={searchTerm}
            onChange={handleSearch}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            className={styles.input}
            role='combobox'
            aria-expanded={showDropdown}
            aria-controls='dropdown-list'
            aria-live='polite'
          />
          {showDropdown && (
            <div
              ref={dropdownRef}
              className={styles.dropdown}
              role='listbox'
              id='dropdown-list'
              onScroll={handleScroll} // Ajout du gestionnaire de défilement
            >
              {visiblePaths.length > 0 ? (
                visiblePaths.map((geo, index) => (
                  <IntLink key={geo.id} href={`/${geo.slug}`} ariaLabel={`Lien vers ${geo.nm}`}>
                    <button className={`${styles.button} ${!searchTerm ? styles.defaultItem : ''} ${index === activeIndex ? styles.activeItem : ''}`} onClick={() => setShowDropdown(false)} role='option' aria-selected={activeIndex === index}>
                      <div className={styles.locInfo}>
                        <span className={styles.locName}>{highlightText(geo.nm, searchTerm)}</span>
                        <span className={styles.geocode}>{highlightText(geo.gkey, searchTerm)}</span>
                      </div>
                    </button>
                  </IntLink>
                ))
              ) : (
                <p className={styles.noResults}>{noResultsText}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SearchSection;
